import React, { useCallback, useEffect, useState } from 'react';
import Dialog from '@mui/material/Dialog';
import DialogContent from '@mui/material/DialogContent';
import useMediaQuery from '@mui/material/useMediaQuery';
import SlideUpTransition from '../dialogs/SlideUpTransition';
import { useTranslation } from 'react-i18next';
import './gameSelector.css';
import TetrisGame from './tetris/tetrisGame';
import SnakeGame from './snake/snakeGame';
import BrickBreakerGame from './brick-breaker/brickBreakerGame';
import { LeaderboardEntry, LeaderboardGameId, fetchTopScoresForAllGames } from '../../services/leaderboardService';
import PacmanGame from './pac-man/pacmanGameImpl';

interface Game {
  id: LeaderboardGameId;
  titleKey: string;
  descriptionKey: string;
  image: string;
  gradient: string;
  component: React.ComponentType<{ onClose?: () => void; onReload?: () => void; onLeaderboardUpdated?: () => void; }>;
}

const games: Game[] = [
  {
    id: 'tetris',
    titleKey: 'gamesCarousel.games.tetris.title',
    descriptionKey: 'gamesCarousel.games.tetris.description',
    image: 'https://www.50plus.de/fileadmin/_processed_/b/a/csm_csm_Tetris_a43af1e949_3e84d23585.jpg',
    gradient: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
    component: TetrisGame,
  },
  {
    id: 'snake',
    titleKey: 'gamesCarousel.games.snake.title',
    descriptionKey: 'gamesCarousel.games.snake.description',
    image: 'https://img.pastemagazine.com/wp-content/uploads/2022/08/17124429/snake_coolmath.jpg',
    gradient: 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)',
    component: SnakeGame,
  },
  {
    id: 'pacman',
    titleKey: 'gamesCarousel.games.pacman.title',
    descriptionKey: 'gamesCarousel.games.pacman.description',
    image: 'https://i.guim.co.uk/img/media/8152f8ea7f06fd8ef5c68a3a594e6ac35dfd774b/0_342_800_480/master/800.jpg',
    gradient: 'linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)',
    component: PacmanGame,
  },
  {
    id: 'brickbreaker',
    titleKey: 'gamesCarousel.games.brickbreaker.title',
    descriptionKey: 'gamesCarousel.games.brickbreaker.description',
    image: 'https://images5.alphacoders.com/379/379950.gif',
    gradient: 'linear-gradient(135deg, #43e97b 0%, #38f9d7 100%)',
    component: BrickBreakerGame,
  },
];

function GameSelecterCarousel({
  launchGameId,
  onCloseRequested,
}: {
  launchGameId?: LeaderboardGameId;
  onCloseRequested?: () => void;
}) {
  const { t } = useTranslation();
  const isCompactScreen = useMediaQuery('(max-width: 768px)');
  const isVerySmallScreen = useMediaQuery('(max-width: 480px)');
  const [selectedGame, setSelectedGame] = useState<Game | null>(null);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [key, setKey] = useState(0);
  const [leaderboards, setLeaderboards] = useState<Record<LeaderboardGameId, LeaderboardEntry[]>>({
    tetris: [],
    snake: [],
    pacman: [],
    brickbreaker: [],
  });
  const [isLoadingLeaderboards, setIsLoadingLeaderboards] = useState(false);
  const isDirectLaunch = !!launchGameId;
  const cardOffset = isVerySmallScreen ? 300 : isCompactScreen ? 350 : 430;

  const loadLeaderboards = useCallback(async () => {
    setIsLoadingLeaderboards(true);
    try {
      const data = await fetchTopScoresForAllGames(games.map((game) => game.id), 3);
      setLeaderboards(data);
    } catch (error) {
      console.error('Failed to load leaderboards:', error);
    } finally {
      setIsLoadingLeaderboards(false);
    }
  }, []);

  useEffect(() => {
    loadLeaderboards();
  }, [loadLeaderboards]);

  useEffect(() => {
    if (!launchGameId) {
      return;
    }

    const launchGame = games.find((game) => game.id === launchGameId);

    if (launchGame) {
      setCurrentIndex(games.findIndex((game) => game.id === launchGameId));
      setSelectedGame(launchGame);
    }
  }, [launchGameId]);

  const nextGame = () => {
    setCurrentIndex((prev) => (prev + 1) % games.length);
  };

  const prevGame = () => {
    setCurrentIndex((prev) => (prev - 1 + games.length) % games.length);
  };

  const selectGame = (game: Game) => {
    setSelectedGame(game);
  };

  const closeDialog = () => {
    setSelectedGame(null);
    setKey(prevKey => prevKey + 1);
  };

  const closeSelectedGame = () => {
    if (isDirectLaunch) {
      onCloseRequested?.();
      return;
    }
    closeDialog();
  };

  const reloadGame = () => {
    setKey(prevKey => prevKey + 1);
  };

  const GameComponent = selectedGame?.component;

  if (isDirectLaunch && GameComponent) {
    return (
      <div className="game-selector-container">
        <div style={{ padding: 0, background: 'transparent', height: '100%', overflow: 'hidden' }}>
          <GameComponent
            key={key}
            onClose={closeSelectedGame}
            onReload={reloadGame}
            onLeaderboardUpdated={loadLeaderboards}
          />
        </div>
      </div>
    );
  }

  return (
    <div className="game-selector-container">
      <div className="game-selector-header">
        <h1>{t('gamesCarousel.title')}</h1>
        <p>{t('gamesCarousel.subtitle')}</p>
      </div>

      <div className="carousel-container">
        <button className="nav-button prev" onClick={prevGame}>
          ‹
        </button>

        <div className="game-cards-wrapper">
          {games.map((game, index) => {
            const offset = index - currentIndex;
            const isActive = index === currentIndex;
            const descriptionText = t(game.descriptionKey);
            
            return (
              <div
                key={game.id}
                className={`game-card ${isActive ? 'active' : ''}`}
                style={{
                  transform: `translateX(${offset * cardOffset}px) scale(${isActive ? 1 : 0.8})`,
                  opacity: Math.abs(offset) > 1 ? 0 : 1,
                  zIndex: isActive ? 10 : 5 - Math.abs(offset),
                }}
                onClick={() => isActive && selectGame(game)}
              >
                <div 
                  className="game-card-background"
                  style={{ background: game.gradient }}
                >
                  <img
                    className="game-card-image"
                    src={game.image}
                    alt={t(game.titleKey)}
                    loading="lazy"
                  />
                  <div className="game-overlay">
                    <h3>{t(game.titleKey)}</h3>
                    <p className="game-description" title={descriptionText}>{descriptionText}</p>
                    {isActive && (
                      <button className="play-button">
                        {t('gamesCarousel.playNow')}
                      </button>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        <button className="nav-button next" onClick={nextGame}>
          ›
        </button>
      </div>

      <div className="game-indicators">
        {games.map((_, index) => (
          <button
            key={index}
            className={`indicator ${index === currentIndex ? 'active' : ''}`}
            onClick={() => setCurrentIndex(index)}
          />
        ))}
      </div>

      <div className="leaderboard-under-indicators">
        <h4>{t('gamesCarousel.topScores')}</h4>
        {isLoadingLeaderboards ? (
          <span className="leaderboard-state">{t('gamesCarousel.loading')}</span>
        ) : leaderboards[games[currentIndex].id]?.length ? (
          <ol>
            {leaderboards[games[currentIndex].id].map((entry, rank) => (
              <li key={`${games[currentIndex].id}-${entry.name}-${rank}`}>
                <span>{entry.name}</span>
                <strong>{entry.score}</strong>
              </li>
            ))}
          </ol>
        ) : (
          <span className="leaderboard-state">{t('gamesCarousel.noScoresYet')}</span>
        )}
      </div>

      <Dialog
        open={!!selectedGame}
        onClose={closeSelectedGame}
        fullScreen={isCompactScreen}
        fullWidth={!isCompactScreen}
        maxWidth={false}
        TransitionComponent={SlideUpTransition}
        sx={{
          '& .MuiDialog-paper': {
            background: 'linear-gradient(135deg, #1e3c72 0%, #2a5298 100%)',
            borderRadius: isCompactScreen ? 0 : '20px',
            border: '1px solid rgba(255, 255, 255, 0.2)',
            height: isCompactScreen ? '100dvh' : '95vh',
            maxHeight: isCompactScreen ? '100dvh' : '95vh',
            width: isCompactScreen ? '100vw' : 'calc(100vw - 4rem)',
            maxWidth: isCompactScreen ? '100vw' : 'calc(100vw - 4rem)',
            margin: isCompactScreen ? 0 : '2rem',
            overflow: 'hidden',
          },
        }}
      >
        <DialogContent key={key} style={{ padding: 0, background: 'transparent', height: '100%', overflow: 'hidden' }}>
          {GameComponent && (
            <GameComponent
              onClose={closeSelectedGame}
              onReload={reloadGame}
              onLeaderboardUpdated={loadLeaderboards}
            />
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

export default GameSelecterCarousel;